import React, { useState, useEffect } from 'react';
import { 
  X, Check, Zap, Shield, Sparkles, CreditCard, Lock, 
  ArrowRight, CheckCircle, Tag, Users, Award
} from 'lucide-react';
import { PRICING_CONFIG, PRO_PLAN_ANNUAL_MONTHLY_PRICE, ENTERPRISE_PLAN_ANNUAL_MONTHLY_PRICE } from '../config/pricing.config';

export type PlanType = 'free' | 'pro' | 'enterprise';
export type BillingCycle = 'monthly' | 'yearly';

interface PlanCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialPlan?: PlanType;
  initialBillingCycle?: BillingCycle;
  currentPlan: PlanType;
  visitorUser?: any;
  onPlanChanged: (newPlan: PlanType) => void;
  onNavigateToWorkspace?: () => void;
}

export default function PlanCheckoutModal({
  isOpen,
  onClose,
  initialPlan = 'pro',
  initialBillingCycle = 'monthly',
  currentPlan,
  visitorUser,
  onPlanChanged,
  onNavigateToWorkspace,
}: PlanCheckoutModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<PlanType>(initialPlan);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>(initialBillingCycle);
  const [email, setEmail] = useState<string>('');
  const [promoCode, setPromoCode] = useState<string>('');
  const [appliedPromo, setAppliedPromo] = useState<{ code: string; discountPercent: number } | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [stripeLoading, setStripeLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showPromoInput, setShowPromoInput] = useState<boolean>(false);

  // Sync state whenever modal opens or props change
  useEffect(() => {
    if (isOpen) {
      setSelectedPlan(initialPlan);
      setBillingCycle(initialBillingCycle);
      setErrorMsg(null);
      setSuccessMsg(null);
      setLoading(false);
      setStripeLoading(false);
      const userEmail = visitorUser?.email || localStorage.getItem('youtube_summarizer_premium_email') || '';
      setEmail(userEmail);
    }
  }, [isOpen, initialPlan, initialBillingCycle, visitorUser]);

  if (!isOpen) return null;

  const isYearly = billingCycle === 'yearly';

  // Calculate pricing
  let basePrice = 0;
  let pricePeriod = '/ month';
  let billingNote = '';

  if (selectedPlan === 'pro') {
    if (isYearly) {
      basePrice = Number(PRO_PLAN_ANNUAL_MONTHLY_PRICE);
      billingNote = `$${(basePrice * 12).toFixed(2)} billed annually (Save 30%)`;
    } else {
      basePrice = PRICING_CONFIG.pro.price;
      billingNote = 'Billed monthly, cancel anytime';
    }
  } else if (selectedPlan === 'enterprise') {
    if (isYearly) {
      basePrice = Number(ENTERPRISE_PLAN_ANNUAL_MONTHLY_PRICE);
      billingNote = `$${(basePrice * 12).toFixed(2)} billed annually (Save 30%)`;
    } else {
      basePrice = PRICING_CONFIG.enterprise.price;
      billingNote = 'Billed monthly, includes full team workspace';
    }
  } else {
    basePrice = 0;
    billingNote = 'Free forever, no credit card required';
  }

  let finalPrice = basePrice;
  if (appliedPromo && basePrice > 0) {
    const discount = (basePrice * appliedPromo.discountPercent) / 100;
    finalPrice = Math.max(0, basePrice - discount);
  }

  const handleApplyPromo = () => {
    setPromoError(null);
    const cleaned = promoCode.trim().toUpperCase();
    if (!cleaned) return;

    // Supported promo codes
    const promoMap: Record<string, number> = {
      'STUDY20': 20,
      'STUDENT50': 50,
      'WELCOME10': 10,
      'PROMO30': 30,
      'ZIPYFREE': 100,
    };

    if (promoMap[cleaned]) {
      setAppliedPromo({ code: cleaned, discountPercent: promoMap[cleaned] });
      setPromoError(null);
    } else {
      setPromoError('Invalid promo code. Try STUDY20 or STUDENT50.');
    }
  };

  const handleInstantSwitch = async () => {
    setLoading(true);
    setErrorMsg(null);

    const targetEmail = (email || visitorUser?.email || 'guest@zipytiny.app').trim().toLowerCase();

    try {
      // 1. Persist subscription to Firestore via backend API
      const response = await fetch('/api/save-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: targetEmail,
          plan: selectedPlan,
          status: selectedPlan === 'free' ? 'canceled' : 'active'
        })
      });

      if (!response.ok) {
        console.warn('Backend save-subscription responded with error, persisting locally.');
      }

      // 2. Persist locally to localStorage
      try {
        localStorage.setItem('youtube_summarizer_plan', selectedPlan);
        if (selectedPlan === 'free') {
          localStorage.removeItem('youtube_summarizer_premium');
        } else {
          localStorage.setItem('youtube_summarizer_premium', 'true');
        }
        localStorage.setItem('youtube_summarizer_premium_email', targetEmail);
      } catch (e) {
        console.warn('localStorage write failed:', e);
      }

      // 3. Update global React state
      onPlanChanged(selectedPlan);

      const planName = selectedPlan === 'enterprise' 
        ? 'Enterprise Plan' 
        : selectedPlan === 'pro' 
        ? 'Pro Plan' 
        : 'Free Workspace';

      setSuccessMsg(`🎉 Plan updated successfully! You are now on the ${planName}.`);
    } catch (err: any) {
      console.error('Instant switch error:', err);
      // Even if network fails, apply locally
      try {
        localStorage.setItem('youtube_summarizer_plan', selectedPlan);
        if (selectedPlan === 'free') {
          localStorage.removeItem('youtube_summarizer_premium');
        } else {
          localStorage.setItem('youtube_summarizer_premium', 'true');
        }
      } catch {}
      onPlanChanged(selectedPlan);
      setSuccessMsg(`Plan activated locally! Welcome to ${selectedPlan.toUpperCase()}.`);
    } finally {
      setLoading(false);
    }
  };

  const handleStripeCheckout = async () => {
    setStripeLoading(true);
    setErrorMsg(null);

    const targetEmail = (email || visitorUser?.email || '').trim().toLowerCase();
    const returnUrl = typeof window !== 'undefined' ? `${window.location.origin}/pricing/` : 'https://zipytiny.app/pricing/';

    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planCode: selectedPlan,
          billingCycle,
          promoCode: appliedPromo?.code || promoCode || undefined,
          email: targetEmail,
          returnUrl
        })
      });

      const data = await response.json();

      if (response.ok && data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
        return;
      }

      // If Stripe server configuration is not setup or returns error, provide friendly fallback
      const errorText = data.error || 'Stripe payment gateway is currently in maintenance.';
      setErrorMsg(
        `${errorText} You can use the "Instant 1-Click Upgrade" button below to activate ${selectedPlan.toUpperCase()} immediately without a card!`
      );
    } catch (err: any) {
      console.error('Stripe checkout error:', err);
      setErrorMsg('Could not reach Stripe checkout server. Please use "Instant 1-Click Upgrade" below.');
    } finally {
      setStripeLoading(false);
    }
  };

  const isCurrentPlan = selectedPlan === currentPlan;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
      <div 
        className="relative w-full max-w-2xl bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-neutral-200 dark:border-zinc-800 overflow-hidden text-neutral-900 dark:text-zinc-100 animate-in fade-in zoom-in-95 duration-200"
        role="dialog"
        aria-modal="true"
      >
        {/* Modal Header */}
        <div className="px-6 py-5 border-b border-neutral-100 dark:border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold text-sm">
              <Zap className="w-4 h-4" />
            </div>
            <div>
              <h2 className="font-extrabold text-lg text-neutral-900 dark:text-zinc-50">
                Choose or Switch Your Plan
              </h2>
              <p className="text-xs text-neutral-500 dark:text-zinc-400">
                Current active plan: <strong className="text-indigo-600 dark:text-indigo-400 capitalize">{currentPlan}</strong>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-xl text-neutral-400 hover:text-neutral-700 dark:hover:text-zinc-200 hover:bg-neutral-100 dark:hover:bg-zinc-800 transition"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success View */}
        {successMsg ? (
          <div className="p-8 text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-950/80 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto shadow-inner">
              <Check className="w-8 h-8 stroke-[3]" />
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-extrabold text-neutral-900 dark:text-white">
                Plan Updated!
              </h3>
              <p className="text-sm text-neutral-600 dark:text-zinc-300 max-w-md mx-auto">
                {successMsg}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              {onNavigateToWorkspace && (
                <button
                  onClick={() => {
                    onClose();
                    onNavigateToWorkspace();
                  }}
                  className="w-full sm:w-auto px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md transition flex items-center justify-center gap-2"
                >
                  <span>Go to Study Workspace</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={onClose}
                className="w-full sm:w-auto px-6 py-3 bg-neutral-100 dark:bg-zinc-800 hover:bg-neutral-200 dark:hover:bg-zinc-700 text-neutral-700 dark:text-zinc-200 rounded-xl text-xs font-bold transition"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
            {/* Error Notification */}
            {errorMsg && (
              <div className="p-3.5 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800/80 rounded-2xl text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                {errorMsg}
              </div>
            )}

            {/* Plan Selector Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Free Card */}
              <button
                type="button"
                onClick={() => setSelectedPlan('free')}
                className={`p-4 rounded-2xl text-left border-2 transition relative flex flex-col justify-between ${
                  selectedPlan === 'free'
                    ? 'border-neutral-900 dark:border-white bg-neutral-50 dark:bg-zinc-800/60 shadow-sm'
                    : 'border-neutral-200 dark:border-zinc-800 hover:border-neutral-300 dark:hover:border-zinc-700'
                }`}
              >
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-neutral-400">
                      Starter
                    </span>
                    {currentPlan === 'free' && (
                      <span className="text-[9px] font-bold bg-neutral-200 dark:bg-zinc-700 px-1.5 py-0.5 rounded text-neutral-700 dark:text-zinc-200">
                        Active
                      </span>
                    )}
                  </div>
                  <h4 className="font-extrabold text-sm text-neutral-900 dark:text-zinc-100">
                    Free Workspace
                  </h4>
                  <div className="mt-2 text-2xl font-black">
                    $0
                  </div>
                  <p className="mt-1 text-[11px] text-neutral-500 dark:text-zinc-400 line-clamp-2">
                    3 summaries/wk, standard flashcards & quizzes.
                  </p>
                </div>
              </button>

              {/* Pro Card */}
              <button
                type="button"
                onClick={() => setSelectedPlan('pro')}
                className={`p-4 rounded-2xl text-left border-2 transition relative flex flex-col justify-between ${
                  selectedPlan === 'pro'
                    ? 'border-[#0071e3] bg-blue-50/50 dark:bg-blue-950/20 shadow-sm'
                    : 'border-neutral-200 dark:border-zinc-800 hover:border-neutral-300 dark:hover:border-zinc-700'
                }`}
              >
                <div className="absolute -top-2.5 right-3 bg-emerald-500 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-xs">
                  Most Popular
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-[#0071e3]">
                      Pro Suite
                    </span>
                    {currentPlan === 'pro' && (
                      <span className="text-[9px] font-bold bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded">
                        Active
                      </span>
                    )}
                  </div>
                  <h4 className="font-extrabold text-sm text-neutral-900 dark:text-zinc-100 flex items-center gap-1">
                    <span>Pro Plan</span>
                    <Zap className="w-3.5 h-3.5 text-[#0071e3] fill-[#0071e3]" />
                  </h4>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-2xl font-black text-[#0071e3]">
                      ${isYearly ? PRO_PLAN_ANNUAL_MONTHLY_PRICE : PRICING_CONFIG.pro.price.toFixed(2)}
                    </span>
                    <span className="text-[10px] text-neutral-500 font-medium">/mo</span>
                  </div>
                  <p className="mt-1 text-[11px] text-neutral-500 dark:text-zinc-400 line-clamp-2">
                    Unlimited uploads, 4h video limits, PPTX & Anki exports.
                  </p>
                </div>
              </button>

              {/* Enterprise Card */}
              <button
                type="button"
                onClick={() => setSelectedPlan('enterprise')}
                className={`p-4 rounded-2xl text-left border-2 transition relative flex flex-col justify-between ${
                  selectedPlan === 'enterprise'
                    ? 'border-purple-600 bg-purple-50/50 dark:bg-purple-950/20 shadow-sm'
                    : 'border-neutral-200 dark:border-zinc-800 hover:border-neutral-300 dark:hover:border-zinc-700'
                }`}
              >
                {isYearly && (
                  <div className="absolute -top-2.5 right-3 bg-emerald-500 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-xs">
                    Save 30%
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400">
                      Team
                    </span>
                    {currentPlan === 'enterprise' && (
                      <span className="text-[9px] font-bold bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded">
                        Active
                      </span>
                    )}
                  </div>
                  <h4 className="font-extrabold text-sm text-neutral-900 dark:text-zinc-100 flex items-center gap-1">
                    <span>Enterprise</span>
                    <Users className="w-3.5 h-3.5 text-purple-600" />
                  </h4>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-2xl font-black text-purple-600 dark:text-purple-400">
                      ${isYearly ? ENTERPRISE_PLAN_ANNUAL_MONTHLY_PRICE : PRICING_CONFIG.enterprise.price}
                    </span>
                    <span className="text-[10px] text-neutral-500 font-medium">/mo</span>
                  </div>
                  <p className="mt-1 text-[11px] text-neutral-500 dark:text-zinc-400 line-clamp-2">
                    Team workspaces, 12h videos, custom templates, dedicated queue.
                  </p>
                </div>
              </button>
            </div>

            {/* Billing Cycle Toggle (For Pro & Enterprise) */}
            {selectedPlan !== 'free' && (
              <div className="flex items-center justify-between bg-neutral-100 dark:bg-zinc-800/80 p-2 rounded-2xl">
                <span className="text-xs font-semibold text-neutral-700 dark:text-zinc-300 px-2">
                  Billing Frequency:
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setBillingCycle('monthly')}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
                      !isYearly 
                        ? 'bg-white dark:bg-zinc-900 text-neutral-900 dark:text-white shadow-xs' 
                        : 'text-neutral-500 dark:text-zinc-400 hover:text-neutral-900'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingCycle('yearly')}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${
                      isYearly 
                        ? 'bg-white dark:bg-zinc-900 text-neutral-900 dark:text-white shadow-xs' 
                        : 'text-neutral-500 dark:text-zinc-400 hover:text-neutral-900'
                    }`}
                  >
                    <span>Annual</span>
                    <span className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded-full">
                      Save 30%
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Selected Plan Details & Features */}
            <div className="bg-neutral-50 dark:bg-zinc-800/40 rounded-2xl p-4 border border-neutral-100 dark:border-zinc-800 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-neutral-400 font-mono">
                    Plan Summary
                  </span>
                  {appliedPromo && (
                    <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-950 px-2 py-0.5 rounded-md">
                      {appliedPromo.discountPercent}% OFF APPLIED
                    </span>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-lg font-black text-neutral-900 dark:text-white">
                    {finalPrice === 0 ? 'Free' : `$${finalPrice.toFixed(2)}${pricePeriod}`}
                  </div>
                  <div className="text-[10px] text-neutral-400">
                    {billingNote}
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t border-neutral-200/60 dark:border-zinc-700/60">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-neutral-700 dark:text-zinc-300">
                  {selectedPlan === 'free' ? (
                    <>
                      <li className="flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                        <span>3 video summaries / week</span>
                      </li>
                      <li className="flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                        <span>Videos up to 30 mins</span>
                      </li>
                      <li className="flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                        <span>Basic flashcards & quizzes</span>
                      </li>
                      <li className="flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                        <span>Custom Gemini API key support</span>
                      </li>
                    </>
                  ) : selectedPlan === 'pro' ? (
                    <>
                      <li className="flex items-center gap-1.5 font-medium">
                        <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        <span>Unlimited video & document uploads</span>
                      </li>
                      <li className="flex items-center gap-1.5 font-medium">
                        <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        <span>Videos up to 4 hours in length</span>
                      </li>
                      <li className="flex items-center gap-1.5 font-medium">
                        <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        <span>PowerPoint (.pptx) & Anki exports</span>
                      </li>
                      <li className="flex items-center gap-1.5 font-medium">
                        <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        <span>High-Speed Gemini 2.5 Flash engine</span>
                      </li>
                      <li className="flex items-center gap-1.5 font-medium">
                        <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        <span>Interactive visual mind maps</span>
                      </li>
                      <li className="flex items-center gap-1.5 font-medium">
                        <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        <span>Audio podcast generation (TTS)</span>
                      </li>
                    </>
                  ) : (
                    <>
                      <li className="flex items-center gap-1.5 font-medium">
                        <Check className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                        <span>Unlimited team member seats</span>
                      </li>
                      <li className="flex items-center gap-1.5 font-medium">
                        <Check className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                        <span>Videos up to 12 hours in length</span>
                      </li>
                      <li className="flex items-center gap-1.5 font-medium">
                        <Check className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                        <span>Custom branded PPTX templates</span>
                      </li>
                      <li className="flex items-center gap-1.5 font-medium">
                        <Check className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                        <span>Dedicated priority processing queue</span>
                      </li>
                      <li className="flex items-center gap-1.5 font-medium">
                        <Check className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                        <span>REST API & Webhook integration</span>
                      </li>
                      <li className="flex items-center gap-1.5 font-medium">
                        <Check className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                        <span>Dedicated priority support</span>
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </div>

            {/* Email association */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-neutral-700 dark:text-zinc-300">
                Account Email for Subscription:
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className="w-full px-3.5 py-2.5 rounded-xl border border-neutral-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs text-neutral-900 dark:text-white placeholder:text-neutral-400 focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-[11px] text-neutral-400">
                Your subscription status is linked to this email and saved in your account.
              </p>
            </div>

            {/* Promo Code section */}
            {selectedPlan !== 'free' && (
              <div>
                {!showPromoInput && !appliedPromo ? (
                  <button
                    type="button"
                    onClick={() => setShowPromoInput(true)}
                    className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                  >
                    <Tag className="w-3 h-3" />
                    <span>Have a promo or discount code?</span>
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      placeholder="e.g. STUDY20"
                      className="px-3 py-1.5 rounded-lg border border-neutral-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs font-mono uppercase focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={handleApplyPromo}
                      className="px-3 py-1.5 bg-neutral-900 dark:bg-zinc-700 text-white rounded-lg text-xs font-bold hover:bg-neutral-800 transition"
                    >
                      Apply
                    </button>
                    {appliedPromo && (
                      <span className="text-xs font-semibold text-emerald-600">
                        ✓ {appliedPromo.code} applied!
                      </span>
                    )}
                  </div>
                )}
                {promoError && (
                  <p className="text-[11px] text-red-500 mt-1">{promoError}</p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-2.5 pt-2">
              {isCurrentPlan ? (
                <div className="p-3 bg-neutral-100 dark:bg-zinc-800/80 rounded-2xl text-center text-xs font-bold text-neutral-600 dark:text-zinc-300 flex items-center justify-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>You are currently on this {selectedPlan.toUpperCase()} plan</span>
                </div>
              ) : (
                <>
                  {/* Primary 1-Click Instant Upgrade/Switch Button */}
                  <button
                    type="button"
                    onClick={handleInstantSwitch}
                    disabled={loading || stripeLoading}
                    className={`w-full py-3.5 rounded-xl text-xs font-extrabold text-white transition shadow-md active:scale-98 flex items-center justify-center gap-2 cursor-pointer ${
                      selectedPlan === 'enterprise'
                        ? 'bg-purple-600 hover:bg-purple-700'
                        : selectedPlan === 'pro'
                        ? 'bg-[#0071e3] hover:bg-[#0077ed]'
                        : 'bg-neutral-800 hover:bg-neutral-900'
                    }`}
                  >
                    {loading ? (
                      <span>Updating Plan...</span>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 fill-white" />
                        <span>
                          {selectedPlan === 'free' 
                            ? 'Switch to Free Workspace' 
                            : `Instant 1-Click Upgrade to ${selectedPlan === 'enterprise' ? 'Enterprise' : 'Pro'}`}
                        </span>
                      </>
                    )}
                  </button>

                  {/* Secondary Stripe Card Checkout Button (for Pro/Enterprise) */}
                  {selectedPlan !== 'free' && (
                    <button
                      type="button"
                      onClick={handleStripeCheckout}
                      disabled={loading || stripeLoading}
                      className="w-full py-3 bg-neutral-100 dark:bg-zinc-800 hover:bg-neutral-200 dark:hover:bg-zinc-700 text-neutral-800 dark:text-zinc-200 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {stripeLoading ? (
                        <span>Connecting to Stripe...</span>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          <span>Pay via Stripe Checkout (Credit Card / Apple Pay)</span>
                        </>
                      )}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Guarantee and Security Notice */}
            <div className="pt-2 flex items-center justify-center gap-4 text-[11px] text-neutral-400 text-center">
              <span className="flex items-center gap-1">
                <Lock className="w-3 h-3 text-emerald-500" />
                <span>256-bit SSL Encryption</span>
              </span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Shield className="w-3 h-3 text-indigo-500" />
                <span>14-day Money-back Guarantee</span>
              </span>
              <span>•</span>
              <span>Cancel or switch anytime</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
